/**
 * 줄서기 화면 공통 유틸 — 외부 URL 스킴 검증 + join 페이로드 정합 가드 + 연락처 표시값 결정.
 *
 * Task 9 코드리뷰 시정(#794): store_meta 로 서버가 내려주는 URL(메뉴/지도/로고)은
 * 신뢰할 수 없는 입력이다 — `javascript:`/`data:` 스킴이나 상대 경로가 섞이면
 * XSS/오픈 리다이렉트로 이어질 수 있어 렌더 직전에 반드시 이 유틸을 거친다.
 */
import type { WaitlistPartyData, WaitlistQueueType } from '@/lib/api/waitlist';
import type { WaitlistJoinInput } from './WaitlistFormScreen';

/**
 * 외부 URL 스킴 검증 — http/https 절대 URL만 통과시킨다.
 *
 * `javascript:`/`data:` 등 실행 가능한 스킴, 상대 경로, 빈 문자열, 파싱 실패는
 * 모두 null 로 축약해 호출부가 "렌더하지 않음"으로 처리하게 한다.
 */
export function safeExternalUrl(url: string | undefined | null): string | null {
  if (!url) {
    return null;
  }

  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return null;
    }
    return url;
  } catch {
    return null;
  }
}

/** join 요청 전 서버로 보낼 최소 페이로드 — party_size/queue_type 정합만 책임진다. */
export interface WaitlistJoinPayload {
  party_size: number;
  queue_type: WaitlistQueueType;
  marketing_consent?: true;
  marketing_notice_version?: string;
  marketing_notice_hash?: string;
  marketing_notice_locale?: string;
}

/**
 * join 제출 페이로드 정합 가드.
 *
 * 테이블 모드는 partySize 가 필수다 — 이전에는 `input.partySize ?? 1` 로 침묵
 * fallback 했는데, 이는 폼 상태가 어떤 이유로든 partySize 를 잃으면 사용자가 모르는
 * 사이 1인석으로 등록되는 결함이었다(#794 Task 9 코드리뷰). 이제 partySize 가
 * 없으면 null 을 반환해 호출부가 제출 자체를 막게 한다.
 *
 * 픽업 모드는 서버가 party_size 를 무시하고 1로 고정하므로(JoinWaitlistRequest)
 * partySize 를 요구하지 않는다.
 *
 * ## 왜 타입 레벨(mode 판별 유니언)로 강화하지 않았는가
 * `WaitlistJoinInput` 은 `WaitlistFormScreen` 밖에서도(FilaVirtualPage, 이 유틸,
 * 향후 테스트) `{ mode: WaitlistQueueType; partySize?: number; ... }` 단순 필드로
 * 광범위하게 참조된다. `mode: 'table'` 일 때만 `partySize` 를 필수로 만드는
 * 판별 유니언으로 바꾸면 FormScreen 의 제출 핸들러(스프레드로 조건부 필드를
 * 구성하는 지점)까지 함께 리팩터링해야 해 이번 시정의 파급 범위를 넘어선다.
 * 런타임 가드 하나로 동일한 불변조건(테이블 모드 ⇒ partySize 필수)을 표현할 수
 * 있으므로 타입 강화는 별도 리팩터링 과제로 남긴다.
 */
export function buildWaitlistJoinPayload(input: WaitlistJoinInput): WaitlistJoinPayload | null {
  let payload: WaitlistJoinPayload;

  if (input.mode === 'table') {
    if (input.partySize === undefined || input.partySize === null) {
      return null;
    }
    payload = { party_size: input.partySize, queue_type: input.mode };
  } else {
    payload = { party_size: 1, queue_type: input.mode };
  }

  if (!input.marketingConsent) {
    return payload;
  }

  return {
    ...payload,
    marketing_consent: true,
    marketing_notice_version: input.marketingConsent.version,
    marketing_notice_hash: input.marketingConsent.hash,
    marketing_notice_locale: input.marketingConsent.locale,
  };
}

/** 마스킹 자리표시 — 서버 표시값(`+52 55 **** 1111`)과 같은 고정 4칸이다. */
const CONTACT_MASK = '****';

/**
 * 장문 형식(앞 2 + **** + 뒤 4)을 쓰는 최소 자리 수 — admin `MaskedE164` 규칙
 * (PR #2510 리뷰 F6)과 동일하다.
 *
 * 가입자번호(국가번호 제외, 트렁크 0 제거 후)가 10자리 이상이면 앞 2 + `****`
 * + 뒤 4 로 가린다. 그보다 짧으면 앞 2자리까지 드러내는 게 과한 노출이 된다 —
 * 예를 들어 7자리 번호에서 앞 2 + 뒤 4 를 드러내면 전체 7자리 중 6자리가
 * 그대로 보여 마스킹의 의미가 없다. 그래서 10자리 미만은 뒤 2자리만 남기고
 * 나머지를 전부 `*` 로 가린다(2자리 이하면 전부 `*`).
 */
const CONTACT_MASK_LONG_FORM_MIN_DIGITS = 10;

/** dial code 정규화 — 숫자만 남기고 `+` 를 붙인다. 숫자가 없으면 빈 문자열. */
function normalizeDialCode(dialCode: string): string {
  const digits = dialCode.replace(/\D/g, '');
  return digits === '' ? '' : `+${digits}`;
}

/**
 * 연락처 마스킹 — 서버(admin#2504, PR #2510 리뷰 F6)와 같은 규칙의 클라이언트 구현.
 *
 * ## 왜 클라이언트에도 있나
 * 이 값은 서버가 상태 응답으로 내려주는 것이 정본이다(`contact_display`). 다만 등록
 * 직후 첫 상태 응답이 도착하기 전의 한 창(window) 동안 화면에는 아무 연락처도 없다 —
 * 그 짧은 구간에 "어디로 알림이 가는지"가 사라지면 고객은 번호를 잘못 넣었는지
 * 확인할 방법을 잃는다. 그래서 **등록 폼이 메모리에 들고 있던 입력값**으로 같은 모양을
 * 만들어 임시로 채운다. 서버 값이 도착하면 그쪽이 이긴다.
 *
 * ## 마스킹 규칙(서버와 동일)
 * 가입자번호(국가번호 제외, 트렁크 0 제거 후) 길이 기준으로 두 형식을 쓴다:
 * - 10자리 이상: `+<국가번호> <앞 2자리> **** <뒤 4자리>` — 예: `+52 55 **** 1111`.
 * - 10자리 미만: `+<국가번호> <뒤 2자리만 남기고 나머지 전부 `*`>` — `*` 개수는
 *   `길이 - 2`(2자리 이하면 전부 `*`). 예: `+502 ******78`(8자리),
 *   `+226 *****67`(7자리).
 *
 * 입력이 비어 있으면 null(= 표시 생략)이다.
 *
 * @param dialCode 국가번호(`+52`). 숫자가 없으면 국가번호 없이 조립한다.
 * @param phoneDigits 국내 번호 숫자열. 구분자와 선행 0(트렁크 프리픽스)은 제거한다.
 */
export function maskWaitlistContact(dialCode: string, phoneDigits: string): string | null {
  const digits = phoneDigits.replace(/\D/g, '').replace(/^0/, '');
  if (digits === '') {
    return null;
  }

  const dial = normalizeDialCode(dialCode);
  const parts =
    digits.length >= CONTACT_MASK_LONG_FORM_MIN_DIGITS
      ? [digits.slice(0, 2), CONTACT_MASK, digits.slice(-4)]
      : [
          digits.length <= 2
            ? '*'.repeat(digits.length)
            : '*'.repeat(digits.length - 2) + digits.slice(-2),
        ];

  return (dial === '' ? parts : [dial, ...parts]).join(' ');
}

/**
 * 상태 화면에 표시할 연락처 한 줄을 고른다 — 표시 우선순위의 단일 정의 지점.
 *
 * 1. `contact_display` — 서버 마스킹 값(정본, admin#2504).
 * 2. `phone_e164` — 원문. 구서버이거나 vault 쓰기 모드 이전이라 서버가 아직 마스킹
 *    값을 만들지 않는 경우다. 이 응답은 access_token 보유자에게만 나가므로 기존과
 *    같은 노출 수준이고, 여기서 임의로 가리면 구서버 사용자에게 정보만 줄어든다.
 * 3. 호출부가 넘긴 폴백 — 등록 폼 입력값을 `maskWaitlistContact` 로 가린 값(메모리).
 * 4. 그 무엇도 없으면 null — 호출부는 행 자체를 렌더하지 않는다.
 *
 * 빈 문자열·공백만 있는 값은 "있다"로 치지 않는다. `??` 만 쓰면 서버가 `''` 를
 * 내려보낼 때 빈 칸이 그대로 화면에 남아 다음 순위로 내려가지 못한다.
 */
export function resolveWaitlistContactDisplay(
  party: Pick<WaitlistPartyData, 'contact_display' | 'phone_e164'>,
  fallback?: string | null,
): string | null {
  for (const candidate of [party.contact_display, party.phone_e164, fallback]) {
    if (typeof candidate === 'string' && candidate.trim() !== '') {
      return candidate;
    }
  }

  return null;
}
